import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ApiService } from '../../services/api.service';
import { firstValueFrom } from 'rxjs';
import { UserService } from '../../services/user.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificationService } from '../../services/notification.service';
import { LoadingService } from '../../services/loading.service';

@Component({
  selector: 'app-purchase-return-modal',
  templateUrl: './purchase-return-modal.component.html',
  styleUrl: './purchase-return-modal.component.scss',
})
export class PurchaseReturnModalComponent implements OnInit {
  @Input() parts: any[] = [];
  @Input() enquiry_id: number;
  @Input() location_id: number;

  selectedParts: number[] = [];
  uploading: boolean;
  loading = false;

  partAttachmentId: { [key: number]: number } = {};
  returnQty: { [key: number]: number } = {};
  remarks: { [key: number]: string } = {};
  showRemarkError: { [key: string]: boolean } = {};

  constructor(
    private activeModal: NgbActiveModal,
    private http: ApiService,
    private user: UserService,
    private notify: NotificationService,
    private loadingService: LoadingService
  ) {}

  ngOnInit(): void {
    if (this.parts && this.parts?.length) {
      console.log('parts', this.parts);
      this.parts.forEach((part) => {
        const id = part.enquiryPartId;

        // Auto-populate returnQty if part already has return_qty
        if (part.return_qty >= 1) {
          this.returnQty[id] = part.return_qty;
          if (!this.selectedParts.includes(id)) {
            this.selectedParts.push(id); // auto-select
          }
        }

        // Also auto-fill remarks if available
        if (part.remarks) {
          this.remarks[id] = part.remarks;
        }
      });
    }
  }

  toggleSelection(partId: number, event: Event) {
    const input = event.target as HTMLInputElement;
    const isChecked = input.checked;

    if (isChecked) {
      this.selectedParts.push(partId);
    } else {
      this.selectedParts = this.selectedParts.filter((id) => id !== partId);
    }
  }

  submitReturn() {
    this.loading = true;
    let hasRemarkError = false;

    // Reset errors
    this.showRemarkError = {};

    const partDetails = this.selectedParts
      .map((partId) => {
        const part = this.parts.find((p) => p.enquiryPartId === partId);
        const return_qty = this.returnQty[partId] || 0;
        const remarks = this.remarks[partId] || '';

        // Check for mandatory remarks
        if (return_qty >= 1 && (!remarks || !remarks.trim())) {
          this.showRemarkError[partId] = true;
          hasRemarkError = true;
        } else {
          this.showRemarkError[partId] = false;
        }

        if (part) {
          part.return_qty = return_qty;
          part.remarks = remarks;
        }

        return {
          partNo: part.partNo || '',
          partDescription: part.partDescription,
          enquiry_part_id: part.enquiryPartId.toString(),
          actual_qty: part.uptoRetQty.toString(),
          return_qty,
          remarks,
          partAttachmentId: part.partAttachmentId,
          partImg: part.partImg,
        };
      })
      .filter((part) => part.return_qty >= 1);

    if (partDetails.length === 0) {
      this.notify.showError('Please enter at least one return quantity.', '');
      this.loading = false;
      return;
    }

    if (hasRemarkError) {
      this.loading = false;
      return;
    }

    const payload = {
      enquiry_id: this.enquiry_id?.toString() || '',
      location_id: this.location_id?.toString() || '',
      partDetails,
    };

    this.http.post('retailers/purchaseReturn', payload).subscribe({
      next: (res: any) => {
        this.notify.showSuccess(res?.message, '');

        this.activeModal.close({ success: true, data: res });
        this.loading = false;
      },
      error: (err) => {
        console.error('API error:', err);
        this.notify.showError(
          'Something went wrong while submitting return.',
          ''
        );
        this.loading = false;
      },
    });
  }

  async addPartImage(index: number) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = async (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 800;
          const maxHeight = 800;

          let { width, height } = img;
          if (width > height && width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          try {
            const blob = await new Promise<Blob>((resolve, reject) => {
              canvas.toBlob(
                (b) => (b ? resolve(b) : reject('Failed to create blob')),
                file.type,
                0.8
              );
            });

            const formData = new FormData();
            formData.append('filename', blob, 'image.png');

            this.loadingService.show();

            const res: any = await firstValueFrom(
              this.http.returnfilePost(
                'retailers/purchaseReturnPartImageUpload',
                formData
              )
            );

            if (res.code == 1) {
              const part = this.parts[index];
              if (part) {
                part.partAttachmentId = res.data.prPartAttachmentId;
                part.partImg = res.data.url;
              }

              this.notify.showSuccess(res.message, '');
            } else {
              this.notify.showError(res.message, '');
            }
          } catch (uploadError) {
            console.error('Upload error:', uploadError);
            this.notify.showError('Error processing image.', '');
          } finally {
            this.loadingService.hide();
          }
        };
      };

      reader.readAsDataURL(file);
    };

    input.click();
  }

  validateReturnQty(partId: number, maxQty: number) {
    const entered = Number(this.returnQty[partId]);
    if (entered > maxQty) {
      this.returnQty[partId] = maxQty;
    } else if (entered < 0) {
      this.returnQty[partId] = 0;
    }
  }

  hasValidReturnData(): boolean {
    return Object.values(this.returnQty).some((qty) => qty >= 1);
  }

  close() {
    this.activeModal.dismiss();
  }
}
